import React from 'react';
import { observer } from 'mobx-react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import * as Roles from '../../Models/Security/Roles';
import ConfigVM from './ConfigVM';
import { AppService, Types } from '../../DomainTypes';
import { EnumHelper, List, ValueObject } from '@singularsystems/neo-core';
import { MandatoryAndVisibleSetting } from '../../Models/Config/FieldConfig/FieldSetting.enum';
import { Catalogue } from '@singularsystems/neo-react-services';
import SARSNatureOfPersonCatalogueEntry from '../Catalogue/General/SARSNatureOfPersonCatalogueEntry';
import { NotificationSettingsComponent } from "@singularsystems/neo-notifications";
import CorrespondenceConfigValues from "../../Models/Config/CorrespondenceConfig";
import { ExportConfig, PRNConfig, RicosConfig } from "../../Models/Config/ExportConfig";
import ImportConfig, { ImportFileType } from '../../Models/Import/ImportConfig';

@observer
export default class ConfigView extends Views.ViewBase<ConfigVM> {

    constructor(props: unknown) {
        super("Config", ConfigVM, props);
    }

    private authorisationService = AppService.get(Types.Neo.Security.AuthorisationService);

    public render() {
        const viewModel = this.viewModel,
            notificationsVM = this.viewModel.notificationsVM;

        return (
            <Neo.TabContainer className="mt-3" selectedTab={this.viewModel.meta.selectedTab}>
                <Neo.Tab header="System Config" icon="cogs" onInitialise={() => this.viewModel.loadConfig()}>
                    <div>
                        <Neo.Card title="System Config">
                            {viewModel.config &&
                                <Neo.Form model={viewModel.config} onSubmit={() => this.viewModel.updateConfig()} showSummaryModal>
                                    <div className="text-end mb-3">
                                        {this.authorisationService.hasRole(Roles.Config.Edit) &&
                                            <Neo.Button pulse={viewModel.config.readyToSave}
                                                variant="primary"
                                                icon="save"
                                                disabled={!viewModel.config.isDirty}
                                                isSubmit>
                                                Save
                                            </Neo.Button>}
                                    </div>
                                    <Neo.GridLayout>
                                        <div>
                                            {viewModel.config?.tenantName && <Neo.FormGroupInline display={viewModel.config.meta.tenantName} />}
                                            <Neo.FormGroupInline bind={viewModel.config.meta.tenantIdentifier}
                                                footerText="Identifier provided by Singular Systems." />

                                            <Neo.FormGroupInline bind={viewModel.config.meta.dividendAccrualProjectionMonths} label="Accrual Projection Months"
                                                footerText="No of months to project accruals into the future." />
                                            <Neo.FormGroupInline label="Enable Pre-Authorisation?" bind={viewModel.config.meta.preAuthEnabled}
                                                footerText="If ticked, requires new records to be pre-authorised before they can be authorised." />
                                            <Neo.FormGroupInline label="Authorisation Required For Transactions?" bind={viewModel.config.meta.authorisationRequiredForTransactions}
                                                footerText="Prevent transactions being created if the investor / issuer is not authorised." />
                                            <Neo.FormGroupInline label="Authorisation Required For Reporting?" bind={viewModel.config.meta.authorisationRequiredForReporting}
                                                footerText="Prevents certain reports / letters from being run / authorised if the investor / issuer is not authorised." />
                                            <Neo.FormGroupInline label="Simple Authorisation?" bind={viewModel.config.meta.allowChangeUserAuthorisations}
                                                footerText="Allows authorisable records to be authorised even if the user authorising the record has made changes to the record." />
                                            <Neo.FormGroupInline label="Default Distribution Wording" bind={viewModel.config.meta.distributionWording}
                                                footerText="This will be the wording displayed throughout the system linked to the type of distributions paid." />
                                            <Neo.FormGroupInline label="System Reminder Days." bind={viewModel.config.meta.systemNoteDays}
                                                footerText="The number of days ahead of events that system reminders should be created." />
                                            <Neo.FormGroupInline label="Dashboard Reminder Days." bind={viewModel.config.meta.reminderDashboardDays}
                                                footerText="The maximum number of days into the future a note reminder date must be in order for it to be included in the reminders dashboard." />
                                        </div>
                                    </Neo.GridLayout>
                                </Neo.Form>}
                        </Neo.Card>
                    </div>
                </Neo.Tab>
                <Neo.Tab
                    header="Reporting Config"
                    icon="far-file"
                    disabled={!this.authorisationService.hasRole(Roles.Config.EditReporting)}
                    onInitialise={() => this.viewModel.loadReportingConfig()}>

                    <div>
                        {this.viewModel.reportingConfig && this.authorisationService.hasRole(Roles.Config.EditReporting) &&
                            <Neo.Form model={this.viewModel.reportingConfig} onSubmit={() => this.viewModel.updateReportingConfig()} showSummaryModal>
                                <div className="text-end mb-3">
                                    <Neo.Button pulse={this.viewModel.reportingConfig.readyToSave}
                                        variant="primary"
                                        icon="save"
                                        disabled={!this.viewModel.reportingConfig.isDirty}
                                        isSubmit>
                                        Save
                                    </Neo.Button>
                                </div>
                                <Neo.GridLayout md={1} lg={2}>
                                    <div>
                                        <Neo.Card title="Entity Received From">
                                            <Catalogue.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.sarsNatureOfPersonId} catalogueType={SARSNatureOfPersonCatalogueEntry} />
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.registeredName} />
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.tradingName} />
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.registrationNumber} />
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.incomeTaxRefNo} />
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.dwtConfig.meta.useForDividendDeclarant}
                                                footerText="If ticked, these details will also be used for the dividend declarant, instead of using issuer details." />
                                        </Neo.Card>
                                    </div>
                                    <div>
                                        <Neo.Card title="Withholding Agent">
                                            <div>
                                                <Neo.FormGroupInline bind={this.viewModel.reportingConfig.withholdingAgentConfig.meta.withholdingAgentRefNumber} />
                                                <Neo.FormGroupInline bind={this.viewModel.reportingConfig.withholdingAgentConfig.meta.beneficiaryName} />
                                                <Neo.FormGroupInline bind={this.viewModel.reportingConfig.withholdingAgentConfig.meta.beneficiaryTaxReferenceNumber} />
                                                <Neo.FormGroupInline bind={this.viewModel.reportingConfig.withholdingAgentConfig.meta.beneficiaryPhysicalAddress} input={{ rows: 3 }} />
                                            </div>
                                        </Neo.Card>
                                    </div>
                                    <div>
                                        <Neo.Card title="Text Values">
                                            <Neo.FormGroupInline bind={this.viewModel.reportingConfig.reportTextConfig.meta.liquidityLadderNotes} input={{ rows: 4 }} />
                                        </Neo.Card>
                                    </div>
                                </Neo.GridLayout>
                            </Neo.Form>}
                    </div>
                </Neo.Tab>
                <Neo.Tab
                    header="Fields Config"
                    icon="bars"
                    disabled={!this.authorisationService.hasRole(Roles.Config.EditField)}
                    onInitialise={() => this.viewModel.loadFieldConfig()}>
                    {this.viewModel.fieldConfig && this.authorisationService.hasRole(Roles.Config.EditField) &&
                        <Neo.Form model={this.viewModel.fieldConfig} onSubmit={() => this.viewModel.updateFieldConfig()} showSummaryModal>
                            <div className="text-end mb-3">
                                <Neo.Button pulse={this.viewModel.fieldConfig.readyToSave}
                                    variant="primary"
                                    icon="save"
                                    disabled={!this.viewModel.fieldConfig.isDirty}
                                    isSubmit>
                                    Save
                                </Neo.Button>
                            </div>
                            <div>
                                {this.renderFieldCardsSection()}
                            </div>
                        </Neo.Form>}
                </Neo.Tab>
                <Neo.Tab header="Notification Setup" icon="envelope" onInitialise={() => notificationsVM.initialise()}>
                    {notificationsVM.notificationSettings &&
                        <Neo.Form model={notificationsVM.notificationSettings} onSubmit={() => notificationsVM.saveNotificationSettings()}>

                            <div className="text-right text-end">
                                <Neo.Button icon="save" isSubmit disabled={!notificationsVM.notificationSettings.isDirty} >Save</Neo.Button>
                            </div>

                            <NotificationSettingsComponent settings={notificationsVM.notificationSettings} />
                        </Neo.Form>}
                </Neo.Tab>
                <Neo.Tab
                    header="Correspondence Settings"
                    icon="inbox"
                    onInitialise={() => this.viewModel.loadCorrespondenceConfig()}>

                    {this.viewModel.correspondenceConfig &&
                        <Neo.Form model={this.viewModel.correspondenceConfig} onSubmit={() => this.viewModel.updateCorrespondenceConfig()} showSummaryModal>
                            <div className="text-end mb-3">
                                {this.authorisationService.hasRole(Roles.Config.Edit) &&
                                    <Neo.Button
                                        pulse={this.viewModel.correspondenceConfig.readyToSave}
                                        variant="primary"
                                        icon="save"
                                        disabled={!this.viewModel.correspondenceConfig.isDirty}
                                        isSubmit>
                                        Save
                                    </Neo.Button>}
                            </div>
                            {this.renderCorrespondenceSettings(this.viewModel.correspondenceConfig)}
                        </Neo.Form>}
                </Neo.Tab>
                <Neo.Tab
                    header="Export Settings"
                    icon="cloud-download-alt"
                    onInitialise={() => this.viewModel.loadExportConfig()}>

                    {this.viewModel.exportConfig && this.renderExportConfig(this.viewModel.exportConfig)}
                </Neo.Tab>
                <Neo.Tab
                    header="Import Settings"
                    icon="cloud-upload-alt"
                    onInitialise={() => this.viewModel.loadImportConfig()}>
                    {this.viewModel.importConfigs && this.renderImportConfigs(this.viewModel.importConfigs)}
                </Neo.Tab>
            </Neo.TabContainer>

        );
    }

    protected renderFieldCardsSection() {
        return (
            <div style={{ marginTop: '-15px' }}>
                <Neo.GridLayout md={2}>
                    <div>
                        {this.renderFieldCard("Investor", this.viewModel.fieldConfig!.investorFields)}
                        {this.renderFieldCard("Investor Investment", this.viewModel.fieldConfig!.investorInvestmentFields)}
                    </div>
                    <div>
                        {this.renderFieldCard("Issuer", this.viewModel.fieldConfig!.issuerFields)}
                        {this.renderFieldCard("Issuer Investment", this.viewModel.fieldConfig!.issuerInvestmentFields)}
                    </div>
                </Neo.GridLayout>
            </div>
        )
    }

    protected renderFieldCard(cardLabel: string, fieldConfig: ValueObject): JSX.Element {
        return (
            <Neo.Card key={cardLabel} title={cardLabel} collapsible>
                {this.renderFieldSettings(fieldConfig)}
            </Neo.Card>);
    }

    protected renderFieldSettings(fieldConfig: ValueObject & { [index: string]: any }): JSX.Element[] {
        const typeInfo = (fieldConfig.constructor as (typeof ValueObject)).neo;

        return typeInfo.properties.filter(prop => !prop.noTrack).map(prop => {
            const propertyInstance = fieldConfig.meta[prop.propertyName];
            const propertyType = Reflect.getMetadata('fieldSetting:type', fieldConfig, prop.propertyName) ?? MandatoryAndVisibleSetting;
            return (
                propertyType === String ?
                    <Neo.FormGroupInline key={prop.propertyName}
                        bind={propertyInstance}
                        label={prop.readableName} /> :
                    <Neo.FormGroupInline key={prop.propertyName}
                        bind={propertyInstance}
                        label={prop.readableName}
                        radioList={{
                            inline: true,
                            items: EnumHelper.asList(propertyType)
                        }} />
            )
        });
    }

    private renderCorrespondenceSettings(values: CorrespondenceConfigValues) {
        return (
            <>
                <Neo.GridLayout md={2}>
                    <Neo.FormGroup bind={values.meta.incomingPortalEmailAddress} input={{ rows: 2 }}
                        footerText="Email address(es) of internal staff who will receive emails from activity on the client portal." />
                    <Neo.FormGroup bind={values.meta.portalUrl}
                        footerText="The url for the client portal used when including links in outgoing emails." />
                </Neo.GridLayout>

                {values.contactUsDetails &&
                    <Neo.GridLayout md={2}>
                        <Neo.Card title="Contact Us Details">
                            <Neo.FormGroup bind={values.contactUsDetails.meta.departmentName} />
                            <Neo.FormGroup bind={values.contactUsDetails.meta.contactNo} />
                            <Neo.FormGroup bind={values.contactUsDetails.meta.address} input={{ rows: 4 }} />
                        </Neo.Card>
                    </Neo.GridLayout>}
            </>
        )
    }

    private renderExportConfig(values: ExportConfig) {

        return (
            <Neo.Form model={values} onSubmit={() => this.viewModel.updateExportConfig()}>

                <div className="text-right text-end">
                    {this.authorisationService.hasRole(Roles.Config.Edit) &&
                        <Neo.Button
                            pulse={values.readyToSave}
                            variant="primary"
                            icon="save"
                            disabled={!values.isDirty}
                            isSubmit>
                            Save
                        </Neo.Button>}
                </div>

                <Neo.Card title="Ricos Config">
                    {!values.ricosConfig &&
                        <Neo.Button onClick={() => values.ricosConfig = new RicosConfig()}>Add Ricos Config</Neo.Button>}
                    {values.ricosConfig &&
                        <>
                            <Neo.FormGroupInline bind={values.ricosConfig.meta.fileNameQA} label="File Name (QA)" />
                            <Neo.FormGroupInline bind={values.ricosConfig.meta.fileNamePROD} label="File Name (PROD)" />
                        </>}
                </Neo.Card>

                <Neo.Card title="PRN Config">
                    {!values.prnConfig &&
                        <Neo.Button onClick={() => values.prnConfig = new PRNConfig()}>Add PRN Config</Neo.Button>}
                    {values.prnConfig &&
                        <>
                            <Neo.FormGroupInline bind={values.prnConfig.meta.fileNameLiability} label="File Name (Liabilities)" />
                            <Neo.FormGroupInline bind={values.prnConfig.meta.fileNameAsset} label="File Name (Assets)" />
                        </>}
                </Neo.Card>
            </Neo.Form>
        )
    }

    private renderImportConfigs(values: List<ImportConfig>) {
        return (
            <Neo.Form model={values} onSubmit={() => this.viewModel.updateImportConfigs()}>

                <div className="text-right text-end">
                    {this.authorisationService.hasRole(Roles.Config.Edit) &&
                        <Neo.Button
                            pulse={values.readyToSave}
                            variant="primary"
                            icon="save"
                            disabled={!values.isDirty}
                            isSubmit>
                            Save
                        </Neo.Button>}
                </div>

                <Neo.Card title="Import Config">
                    <NeoGrid.Grid items={values} showAddButton >
                        {(item, meta) => (
                            <NeoGrid.Row>
                                <NeoGrid.Column bind={meta.name} />
                                <NeoGrid.Column
                                    bind={meta.columnHeadings}
                                    label={"Heading Row?"}
                                    onSelect={() => {
                                        if (!item.columnHeadings) {
                                            item.rowFilterColumnName = null;
                                            item.effectiveDateColumnName = null;
                                            item.interestRateColumnName = null;
                                        }
                                    }} />
                                <NeoGrid.Column bind={meta.interestRateColumnIndex} label={"Rate Index"} />
                                <NeoGrid.Column bind={meta.interestRateColumnName} label={"Rate Column Name"} disabled={!item.columnHeadings} />
                                <NeoGrid.Column bind={meta.effectiveDateColumnIndex} label={"Date Index"} />
                                <NeoGrid.Column bind={meta.effectiveDateColumnName} label={"Date Column Name"} disabled={!item.columnHeadings} />
                                <NeoGrid.Column bind={meta.rowFilterColumnIndex} label={"Filter Index"} />
                                <NeoGrid.Column bind={meta.rowFilterColumnName} label={"Filter Column Name"} disabled={!item.columnHeadings} />
                                <NeoGrid.Column
                                    bind={meta.importFileType}
                                    select={{ items: EnumHelper.asList(ImportFileType) }}
                                    label={"File Type"}
                                    onSelect={() => {
                                        if (item.importFileType != ImportFileType.Csv) {
                                            item.delimiter = null;
                                        }
                                    }} />
                                <NeoGrid.Column bind={meta.delimiter} label={"Field Delimiter"} disabled={item.importFileType != ImportFileType.Csv} />
                                <NeoGrid.Column bind={meta.automatedImport} label="Automated Import?" cellTooltip="Indicates that this import config will be used to automatically update interest rate types." />
                                <NeoGrid.ButtonColumn showDelete />
                            </NeoGrid.Row>
                        )}
                    </NeoGrid.Grid>
                </Neo.Card>
            </Neo.Form>
        )
    }

    public onLeave(): string | undefined {
        if (this.viewModel.config?.isDirty || this.viewModel.reportingConfig?.isDirty || this.viewModel.fieldConfig?.isDirty || this.viewModel.exportConfig?.isDirty || this.viewModel.importConfigs.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}